import {
  CompactOrderResponse,
  OrderActivityPageQuery,
  OrderEstimateId,
  OrderId,
  OrderProductId,
  OrderRequest,
  OrderResponse,
  OrderStageId,
  OrderVendorId,
} from '@business/entities/order';
import { OrgId } from '@business/entities/organization';
import { InvoiceId } from '@business/entities/TransactionInvoice';
import { CommonService } from '@business/services';
import { R } from '@core/utils/r';
import { Endpoint } from '../endpoint';
import { Paginate } from './../../entities/common/Paginate';
import { OrderActivityResponse } from './../../entities/order/OrderActivityResponse';
import {
  OrderCancelRequest,
  OrderEmailTemplateRequest,
  OrderShippingRequest,
  OrderStatus,
  OrderWithItemsResposne,
} from './../../entities/order/OrderResponse';
import { OrderShipmentRequest, OrderShipmentStatus } from './../../entities/order/OrderShipmentResponse';
import { PoType, PurchaseOrderId } from './../../entities/order/PurchaseOrderResponse';

export function fetchOrders(orgId?: OrgId, status?: OrderStatus) {
  return Endpoint.get<CompactOrderResponse[]>(`@gc/orders`, { params: R.objectCompact({ orgId, status }) });
}
export function fetchOrgOrders(orgId?: OrgId) {
  return Endpoint.get<OrderResponse[]>(`@gc/orders/org`, { params: { orgId } });
}

export function searchOrders(search: string) {
  return Endpoint.get<OrderResponse[]>(`@gc/orders/search`, { params: { search } });
}

export function fetchOrder(id: OrderId, fromNgrok = false) {
  //   if (fromNgrok) {
  //     return Endpoint.get<OrderResponse>(`@ngrok/orders/order/${id}`);
  //   }
  return Endpoint.get<OrderResponse>(`@gc/orders/order/${id}`);
}

export function createOrder(data: OrderRequest) {
  const formData = CommonService.toFormData(data);
  return Endpoint.post<OrderResponse>(`@gc/orders`, { formData, hasFiles: true });
}

export function updateOrder(id: OrderId, data: Partial<OrderRequest>, isSend = false) {
  return Endpoint.patch<OrderResponse>(`@gc/orders/${id}`, { data: { ...data, isSend } });
}

export function updateEstimate(id: OrderId, data: OrderRequest) {
  const formData = CommonService.toFormData(data);
  formData.append('_method', 'patch');
  return Endpoint.post<OrderResponse>(`@gc/orders/estimate/${id}`, { formData, hasFiles: true });
}

export function updateInvoice(id: InvoiceId, data: OrderRequest) {
  const formData = CommonService.toFormData(data);
  formData.append('_method', 'patch');
  return Endpoint.post<OrderResponse>(`@gc/order-invoice/${id}`, { formData, hasFiles: true });
}

export function createInvoice(id: OrderId, data: OrderRequest) {
  const formData = CommonService.toFormData(data);
  return Endpoint.post<OrderResponse>(`@gc/order-invoice/${id}`, { formData, hasFiles: true });
}

export function deleteOrder(id: OrderProductId) {
  return Endpoint.delete<void>(`@gc/orders/${id}`);
}
export function acceptEstimate(id: OrderEstimateId, name: string, email: string) {
  return Endpoint.post<OrderResponse>(`@gc/orders/accepted/${id}`, { data: { name, email } });
}
export function createOrderInvoice(id: OrderEstimateId, invoiceId: InvoiceId, sent: boolean, data?: Partial<OrderRequest>) {
  const formData = CommonService.toFormData(data);
  formData.append('sent', sent ? 'true' : 'false');
  formData.append('invoiceId', invoiceId);
  return Endpoint.post<OrderResponse>(`@gc/order-invoice/send/${id}`, { formData, hasFiles: true });
}

export function sendEstimate(id: OrderEstimateId, data?: Partial<OrderRequest>) {
  return Endpoint.post<OrderResponse>(`@gc/orders/estimate-send/${id}`, { data });
}

export function updateStage(
  id: OrderId,
  stage: OrderStageId,
  deliveryDate?: number,
  closeIt = false,
  sendIt?: boolean,
  emailData?: OrderEmailTemplateRequest,
) {
  const formData = CommonService.toFormData({ ...emailData, stage, deliveryDate, closeIt, sendIt });
  return Endpoint.post<OrderResponse>(`@gc/orders/stage/${id}`, { formData, hasFiles: true });
}

export function updateStatus(id: OrderId, status: OrderStatus, reason?: string, isSend?: boolean, data?: OrderCancelRequest) {
  return Endpoint.post<OrderResponse>(`@gc/orders/status/${id}`, { data: { ...data, status, isSend } });
}

export function updateShipping(id: OrderId, stage: OrderStageId, data: OrderShippingRequest) {
  const formData = CommonService.toFormData(data);
  return Endpoint.post<OrderResponse>(`@gc/orders/shipping/${id}/${stage}`, { formData, hasFiles: true });
}

export function rejectEstimate(id: OrderEstimateId, reason: string) {
  return Endpoint.post<OrderResponse>(`@gc/orders/rejected/${id}`, { data: { reason } });
}
export function sendPurchaseOrder(id: OrderId, vendorId: OrderVendorId, type: PoType, data: Partial<OrderEmailTemplateRequest>) {
  const formData = CommonService.toFormData({ ...data, type });
  formData.append('vendorId', vendorId || '0');
  return Endpoint.post<OrderResponse>(`@gc/orders/vendor-order/${id}`, { formData, hasFiles: true });
}

export function createPurchaseOrder(id: OrderId, vendorId?: OrderVendorId) {
  return Endpoint.post<OrderResponse>(`@gc/orders/po-create/${id}`, { data: { vendorId } });
}

export function closePurchaseOrder(id: PurchaseOrderId) {
  return Endpoint.post<OrderResponse>(`@gc/orders/purchase-close/${id}`, { data: {} });
}
export function download(orderId: OrderId, vendorId: OrderVendorId, filename: string, type: PoType) {
  return Endpoint.get<void>(`@gc/orders/po-download/${orderId}/${vendorId}`, { isFile: true, filename, params: { type } });
}

export function downloadLading(id: PurchaseOrderId, filename: string) {
  return Endpoint.get<void>(`@gc/purchase-orders/lading/${id}`, { isFile: true, filename });
}

export function downloadPickList(id: PurchaseOrderId, filename: string) {
  return Endpoint.get<void>(`@gc/purchase-orders/pick-list/${id}`, { isFile: true, filename });
}

export function purchaseOrderStatus(poId: PurchaseOrderId, status: OrderShipmentStatus, data: Partial<OrderShipmentRequest>) {
  return Endpoint.post<OrderResponse>(`@gc/orders/po-status/${poId}`, { data: { ...data, status } });
}

export function updatePurchaseOrder(id: PurchaseOrderId, data: Partial<OrderRequest>) {
  const formData = CommonService.toFormData(data);
  formData.append('_method', 'patch');
  return Endpoint.post<OrderResponse>(`@gc/purchase-orders/${id}`, { formData, hasFiles: true });
}

export function saveBillOfLading(id: PurchaseOrderId, data: Partial<OrderRequest>) {
  return Endpoint.patch<OrderResponse>(`@gc/purchase-orders/pick-list/${id}`, { data });
}

export function savePickList(id: PurchaseOrderId, data: Partial<OrderRequest>) {
  return Endpoint.patch<OrderResponse>(`@gc/purchase-orders/pick-list/${id}`, { data });
}

export function createShipment(poId: PurchaseOrderId, status: OrderShipmentStatus, data: Partial<OrderShipmentRequest>, isSend?: boolean) {
  const formData = CommonService.toFormData({ ...data, status, isSend });
  return Endpoint.post<OrderResponse>(`@gc/orders/shipment/${poId}`, { formData, hasFiles: true });
}

export function fetchActivities(options: OrderActivityPageQuery) {
  const params = R.objectCompact(options);
  return Endpoint.get<OrderActivityResponse[]>(`@gc/order-activity`, { params });
}

export function fetchPaginateActivities(options: OrderActivityPageQuery) {
  const params = R.objectCompact(options);
  return Endpoint.get<Paginate<OrderActivityResponse>>(`@gc/order-activity`, { params });
}

export function updateShipment(id: OrderId, data: OrderShippingRequest, isSend?: boolean) {
  const formData = CommonService.toFormData({ ...data, isSend });
  formData.append('_method', 'patch');
  return Endpoint.post<OrderResponse>(`@gc/orders/shipment/${id}`, { formData, hasFiles: true });
}

export function deleteShipment(id: OrderId) {
  return Endpoint.delete<OrderResponse>(`@gc/orders/shipment/${id}`);
}

export function deleteInvoice(invoiceId: InvoiceId, orderId: OrderId) {
  return Endpoint.delete<OrderResponse>(`@gc/orders/invoice/${invoiceId}`, { data: { orderId } });
}

export function openedEstimate(id: OrderEstimateId) {
  return Endpoint.post<void>(`@gc/orders/estimate-opened/${id}`);
}

export function searchOrderItems(search: string) {
  return Endpoint.get<OrderWithItemsResposne[]>(`@gc/orders/search-order`, { params: { search } });
}
